import { useState } from "react";
import type { Celebrity, CelebrityInput } from "@pushup/types";
import { DIFFICULTY_LABELS } from "@pushup/types";
import { Badge } from "@pushup/ui";
import { Button } from "@pushup/ui";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { CelebrityForm } from "./celebrity-form";

export interface CelebrityRowProps {
  celebrity: Celebrity;
  isUpdating: boolean;
  isDeleting: boolean;
  updateError?: string | null;
  onUpdate: (input: CelebrityInput) => void;
  onDelete: () => void;
}

export function CelebrityRow({
  celebrity,
  isUpdating,
  isDeleting,
  updateError = null,
  onUpdate,
  onDelete,
}: CelebrityRowProps) {
  const [editing, setEditing] = useState(false);

  const input: CelebrityInput = {
    name: celebrity.name,
    imageDataUrl: celebrity.imageDataUrl,
    difficulty: celebrity.difficulty,
    secondsPerRep: celebrity.secondsPerRep,
    workoutDurationSec: celebrity.workoutDurationSec,
    voicePhases: celebrity.voicePhases,
  };

  return (
    <div className="widget">
      <div className="widget-body space-y-4">
        <div className="flex items-center gap-4">
          {celebrity.imageDataUrl ? (
            <img
              src={celebrity.imageDataUrl}
              alt={celebrity.name}
              className="size-12 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white/10 text-lg font-bold text-white">
              {celebrity.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate font-semibold text-white">{celebrity.name}</span>
              <Badge variant="secondary">{DIFFICULTY_LABELS[celebrity.difficulty]}</Badge>
              <span className="text-xs text-foreground/60">{celebrity.secondsPerRep}s/rep</span>
              <span className="text-xs text-foreground/60">
                {celebrity.voicePhases.length} clips
              </span>
              <span className="text-xs text-foreground/60">
                {celebrity.workoutDurationSec > 0
                  ? `${celebrity.workoutDurationSec}s round`
                  : "endless round"}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setEditing((current) => !current)}
              aria-label="Edit"
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={onDelete}
              disabled={isDeleting}
              aria-label={`Delete ${celebrity.name}`}
            >
              {isDeleting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
            </Button>
          </div>
        </div>

        {editing && (
          <CelebrityForm
            initial={input}
            isSubmitting={isUpdating}
            error={updateError}
            onSave={onUpdate}
            onCancel={() => setEditing(false)}
          />
        )}
      </div>
    </div>
  );
}
